/**
 * Fetch a one-time Univer Workspace (Sheets) SSO URL from Control Plane.
 *
 * Contract: .claude/plans/1966-univer-workspace-sheets/context-contract-sso.md §1 + §4 (+ Deviations log).
 *   POST control_plane.api.univer.get_sso_url  { access_token, tenant_name: <CP tenant id = JWT tenant>, email }
 *   → message: { success: true, sso_token, expires_in, url: "https://sheets.zaviago.com/api/auth/zaviago/sso?token=…" }
 *   → message: { success: false, error, error_code }  — HTTP 200 for EVERY business failure, so the
 *     result is read from `message.success` / `message.error_code`, never from `response.ok`.
 *
 * Goes through `cpGuestApiCall` (the shared CP guest-call helper): JWT in the body, no
 * Authorization header, `credentials: "omit"`, one 401 refresh-and-retry, and the
 * `_server_messages` double-JSON error parsing. Callers must `await ensureFreshToken()`
 * first (frappe-debug #62 refresh race).
 *
 * The URL is single-use and expires in 5 minutes — NEVER cache it. Mint a new one on
 * every iframe mount / retry.
 */

import { cpGuestApiCall, getCPToken } from "@/lib/api/controlPlaneClient";

/** Origin of the Univer Workspace deployment. Also the only allowed postMessage origin. */
export const UNIVER_ORIGIN = new URL(
  import.meta.env.VITE_UNIVER_URL || "https://sheets.zaviago.com"
).origin;

/** CP business codes (contract Deviations log) + client-side codes. */
export type UniverSsoErrorCode =
  | "INVALID_TOKEN"
  | "EMAIL_MISMATCH"
  | "TENANT_MISMATCH"
  | "TENANT_NOT_FOUND"
  | "TENANT_INACTIVE"
  | "UNIVER_NOT_ENABLED"
  | "NOT_A_MEMBER"
  | "NOT_AUTHENTICATED"
  | "BAD_RESPONSE"
  | "REQUEST_FAILED"
  | (string & {}); // future CP codes

export class UniverSsoError extends Error {
  readonly code: UniverSsoErrorCode;

  constructor(code: UniverSsoErrorCode, message: string) {
    super(message);
    this.name = "UniverSsoError";
    this.code = code;
  }
}

interface UniverSsoMessage {
  success?: boolean;
  sso_token?: string;
  url?: string;
  expires_in?: number;
  error?: string;
  error_code?: string;
}

export interface UniverSsoResult {
  url: string;
  expiresIn: number;
}

export async function fetchUniverSso(
  tenantName: string,
  userEmail: string
): Promise<UniverSsoResult> {
  const token = getCPToken();
  if (!token) {
    throw new UniverSsoError("NOT_AUTHENTICATED", "Not authenticated to Control Plane");
  }

  let data: UniverSsoMessage | undefined;
  try {
    data = await cpGuestApiCall<UniverSsoMessage>(
      "control_plane.api.univer.get_sso_url",
      {
        method: "POST",
        body: JSON.stringify({
          access_token: token,
          tenant_name: tenantName,
          email: userEmail,
        }),
      }
    );
  } catch (err) {
    // Transport / server crash (non-2xx). Business failures never land here (HTTP 200).
    const message = err instanceof Error ? err.message : "Failed to get Sheets SSO URL";
    throw new UniverSsoError("REQUEST_FAILED", message);
  }

  if (!data?.success) {
    throw new UniverSsoError(
      data?.error_code || "REQUEST_FAILED",
      data?.error || "Failed to get Sheets SSO URL"
    );
  }

  // Only ever frame the Univer origin — never an arbitrary URL from a response.
  let parsed: URL;
  try {
    parsed = new URL(data.url ?? "");
  } catch {
    throw new UniverSsoError("BAD_RESPONSE", "Sheets SSO URL is missing or invalid");
  }
  if (parsed.origin !== UNIVER_ORIGIN) {
    throw new UniverSsoError("BAD_RESPONSE", `Unexpected Sheets origin: ${parsed.origin}`);
  }

  return { url: parsed.toString(), expiresIn: data.expires_in ?? 300 };
}
